import { Loader } from "@package/subframe/components/Loader";
import { FeatherChevronRight, FeatherFilePlus } from "@subframe/core";
import { Link } from "@tanstack/react-router";

import { isLaunchedEventType } from "@/components/event-records/event-record-schema";
import { useAuth } from "@/contexts/AuthContext";
import { useNursery } from "@/contexts/NurseryContext";
import { useOrganisation } from "@/contexts/OrganisationContext";
import { useListEventTypesQueryControllerList } from "@/generated/event-types/event-types";
import { canReportEvents } from "@/lib/roles";

/**
 * The "Report an event" entry point — one row per launched event type (NAU-347).
 * Rendered as the Workspace's Events tab (NAU-442).
 *
 * Never wrap this in a FeatureGate: recording a fire drill is a legal
 * requirement, so Launch-plan nurseries must keep it.
 */
export function ReportEventSection() {
  const { selectedNurseryId, nurseryRole } = useNursery();
  const { isPlatformAdmin } = useAuth();
  const { isOrgAdmin } = useOrganisation();

  const userCanReportEvents = canReportEvents({
    isPlatformAdmin,
    isOrgAdmin,
    nurseryRole,
  });
  const { data: eventTypesResponse, isLoading } =
    useListEventTypesQueryControllerList(selectedNurseryId ?? "", {
      query: { enabled: !!selectedNurseryId && userCanReportEvents },
    });
  const eventTypes = (eventTypesResponse?.data?.items ?? []).filter((t) =>
    isLaunchedEventType(t.slug),
  );

  if (!userCanReportEvents) return null;
  if (isLoading) {
    return (
      <div className="flex w-full items-center justify-center py-12">
        <Loader size="large" />
      </div>
    );
  }
  if (!eventTypesResponse) return null;

  return (
    <div className="flex w-full flex-col items-start gap-2">
      <span className="text-heading-3 font-heading-3 text-default-font">
        Report an event
      </span>
      {eventTypes.length === 0 && (
        <span className="text-body font-body text-subtext-color">
          No event types configured for this nursery
        </span>
      )}
      {eventTypes.map((eventType) => (
        <Link
          key={eventType.id}
          to="/event-records/new"
          search={{ eventTypeId: eventType.id }}
          className="w-full"
        >
          <div className="flex w-full items-center gap-3 rounded-lg border border-neutral-200 px-4 py-3 hover:bg-neutral-50 transition-colors cursor-pointer">
            <FeatherFilePlus className="h-4 w-4 text-subtext-color" />
            <div className="flex grow flex-col items-start gap-0.5">
              <span className="text-body-bold font-body-bold text-default-font">
                {eventType.name}
              </span>
              {eventType.description && (
                <span className="text-caption font-caption text-subtext-color">
                  {eventType.description}
                </span>
              )}
            </div>
            <FeatherChevronRight className="h-4 w-4 text-subtext-color" />
          </div>
        </Link>
      ))}
    </div>
  );
}
